import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  Input,
  Select,
  Option,
  Typography,
  Chip,
} from "@material-tailwind/react";
import { useNavigate } from "react-router-dom";

import { useMutation } from "react-query";
import { Link, useLocation } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { debounce } from "lodash";
import { useDispatch } from "react-redux";
import { BsStar, BsStarFill } from "react-icons/bs";
import { RiInbox2Line } from "react-icons/ri";
import http, { httpMushPro } from "../../services/http-common";
import { toast } from "react-toastify";
import {
  appointmentStatus,
  formatFullTime,
  timeAgo,
} from "../../utils/dateFormat";
import { setSelectedSeekerDetails } from "../../store/dataFlow/actions";

const SeekerPriority = () => {
  const location = useLocation();
  const history = useNavigate();
  const dispatch = useDispatch();
  const [listSeekersPriority, setListSeekersPriority] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("firstName");
  const [open, setOpen] = useState(false);
  const [seekerMail, setSeekerMail] = useState("");
  const [searchSeekers, setSearchSeekers] = useState({
    firstName: null,
    appointmentStatus: null,
    lastInteractionDate: null,
    pinned: null,
  });

  const getAppointments = useMutation(
    () => {
      return http.post("user/contact/fetch-seekers", {
        pinned: true,
      });
    },
    {
      onSuccess: ({ data }) => {
        setListSeekersPriority(data.rows);
      },
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );

  const getSeekersList = useMutation(
    () => {
      return httpMushPro.post("user/scheduling/search-seekers", searchSeekers);
    },
    {
      onSuccess: ({ data }) => {
        setListSeekersPriority(data.appointments);
      },
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );

  const toggleModal = () => setOpen(!open);
  const handleDetails = (details) => {
    dispatch(setSelectedSeekerDetails(details));
    history("/seekers/details");
  };

  const inviteSeeker = useMutation(
    (mail) => {
      return http.post("user/seeker-invite/create", {
        seekerEmail: mail,
      });
    },
    {
      onSuccess: () => {
        toast.success("Invitation mail sent successfully!");
        setSeekerMail("");
        setOpen(false);
      },
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );
  const updatePriority = useMutation(
    (data) => {
      return httpMushPro.put("user/scheduling/update-priority", {
        id: data.seekerId,
        pinned: data.value,
      });
    },
    {
      onSuccess: () => {
        getSeekersList.mutate();
        toast.success("Updated successfully!");
      },
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );

  const handleSubmit = () => inviteSeeker.mutate(seekerMail);

  const onChangeSelectedFilter = (val) => setSelectedFilter(val);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedConsoleLog = useCallback(
    debounce(() => {
      getSeekersList.mutate();
    }, 500),
    []
  );

  const handleSearchChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      setSearchSeekers((prev) => {
        const updatedSearchSeekers = Object.keys(prev).reduce((acc, key) => {
          acc[key] = null;
          return acc;
        }, {});

        updatedSearchSeekers[name] = value;

        debouncedConsoleLog(updatedSearchSeekers);
        return updatedSearchSeekers;
      });
    },
    [debouncedConsoleLog]
  );

  const onChangeSelectedFilterValue = useCallback(
    (name, value) => {
      setSearchSeekers((prev) => {
        const updatedSearchSeekers = Object.keys(prev).reduce((acc, key) => {
          acc[key] = null;
          return acc;
        }, {});

        updatedSearchSeekers[name] = value;

        debouncedConsoleLog(updatedSearchSeekers);
        return updatedSearchSeekers;
      });
    },
    [debouncedConsoleLog]
  );
  const filterOptions = useMemo(
    () => ({
      firstName: (
        <Input
          name="firstName"
          size="lg"
          icon={<MagnifyingGlassIcon className="h-5 w-5" />}
          placeholder="Search"
          className="!border !border-gray-300 bg-white text-gray-900 shadow-lg shadow-gray-900/5 ring-4 ring-transparent placeholder:text-gray-500 focus:!border-gray-900 focus:!border-t-gray-900 focus:ring-gray-900/10"
          labelProps={{ className: "hidden" }}
          containerProps={{ className: "min-w-[400px]" }}
          onChange={handleSearchChange}
        />
      ),
      appointmentStatus: (
        <Select
          value={searchSeekers.appointmentStatus}
          onChange={(v) => onChangeSelectedFilterValue("appointmentStatus", v)}
        >
          <Option value="SCHEDULED">Scheduled</Option>
          <Option value="PENDING">Pending</Option>
          <Option value="CANCELLED">Canceled</Option>
        </Select>
      ),
      pinned: (
        <Select
          value={searchSeekers.pinned}
          onChange={(v) => onChangeSelectedFilterValue("pinned", v)}
        >
          <Option value="true">High</Option>
          <Option value="false">Low</Option>
        </Select>
      ),
      lastInteractionDate: (
        <Input
          name="lastInteractionDate"
          size="lg"
          type="date"
          value={searchSeekers.lastInteractionDate}
          placeholder="Search"
          className="!border !border-gray-300 bg-white text-gray-900 shadow-lg shadow-gray-900/5 ring-4 ring-transparent placeholder:text-gray-500 focus:!border-gray-900 focus:!border-t-gray-900 focus:ring-gray-900/10"
          labelProps={{ className: "hidden" }}
          containerProps={{ className: "min-w-[400px]" }}
          onChange={handleSearchChange}
        />
      ),
    }),
    [searchSeekers, handleSearchChange, onChangeSelectedFilterValue]
  );

  useEffect(() => {
    location.pathname === "/dashboard"
      ? getAppointments.mutate()
      : getSeekersList.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <div
            className={`flex justify-between items-center ${
              location.pathname !== "/dashboard" && "pt-3"
            } gap-2 flex-col sm:flex-row`}
          >
            <h4 className="card-title mb-0">
              {" "}
              {location.pathname === "/dashboard" ? "Priority " : "All "}{" "}
              Seekers
            </h4>
            {location.pathname !== "/dashboard" && (
              <div className="flex items-center gap-2 flex-col sm:flex-row">
                <div className="min-w-[220px]  max-w-[220px] first:[&>div]:sm:!min-w-[120px] first:[&>div]:min-w-auto">
                  <Select
                    label="Search by"
                    value={selectedFilter}
                    onChange={onChangeSelectedFilter}
                  >
                    <Option value="pinned">Priority</Option>
                    <Option value="firstName">EntheoID</Option>
                    <Option value="lastInteractionDate">
                      Last Interaction Date
                    </Option>
                    <Option value="appointmentStatus">
                      Appointment Status
                    </Option>
                  </Select>
                </div>
                {filterOptions[selectedFilter]}
              </div>
            )}

            <Button
              size="sm"
              className="!bg-[#000080] text-center"
              onClick={toggleModal}
            >
              + Invite Seeker
            </Button>
          </div>

          <div
            className={`table-responsive ${
              location.pathname !== "/dashboard" && "mt-4"
            }`}
          >
            {listSeekersPriority && listSeekersPriority.length !== 0 ? (
              <table className="table-auto w-full text-left">
                <thead className="bg-gray-100">
                  <tr>
                    {location.pathname !== "/dashboard" && (
                      <>
                        <th className="px-4 py-2">Priority</th>
                        <th className="px-4 py-2">EntheoID</th>
                        <th className="px-4 py-2">Last Interaction</th>
                        <th className="px-4 py-2">Appointment Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {listSeekersPriority?.map((appointement, key) => (
                    <>
                      {location.pathname === "/dashboard" ? (
                        <tr key={key}>
                          <td className="px-4 py-2">
                            <div className="flex flex-col gap-1">
                              <span className="font-bold text-sm">
                                {" "}
                                {appointement?.user?.firstName}
                              </span>
                              <span>
                                Validity:{" "}
                                {formatFullTime(appointement.validityDate)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        <>
                          <tr key={key}>
                            <td className="px-4 py-2">
                              <div className="flex flex-col gap-1">
                                <span className="flex items-center gap-2 font-bold text-sm">
                                  {" "}
                                  {appointement?.attendees?.pinned ? (
                                    <BsStarFill
                                      onClick={() => {
                                        updatePriority.mutate({
                                          seekerId: appointement?.id,
                                          value:
                                            !appointement?.attendees?.pinned,
                                        });
                                      }}
                                      className="text-[#fab800] w-4 h-4 cursor-pointer"
                                    />
                                  ) : (
                                    <BsStar
                                      onClick={() => {
                                        updatePriority.mutate({
                                          seekerId: appointement?.id,
                                          value:
                                            !appointement?.attendees?.pinned,
                                        });
                                      }}
                                      className="w-4 h-4 cursor-pointer"
                                    />
                                  )}
                                  {appointement?.attendees?.pinned
                                    ? "High"
                                    : "Low"}
                                </span>
                              </div>
                            </td>
                            <td
                              className="px-4 py-2"
                              onClick={() => handleDetails(appointement)}
                            >
                              <div className="flex flex-col gap-1">
                                <span className="font-bold text-sm">
                                  {" "}
                                  {appointement?.attendees?.user?.firstName}
                                </span>
                              </div>
                            </td>
                            <td
                              className="px-4 py-2"
                              onClick={() => handleDetails(appointement)}
                            >
                              <span className="font-bold text-sm">
                                {" "}
                                {timeAgo(appointement?.lastInteraction)}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              <Chip
                                size="sm"
                                variant="gradient"
                                color={appointmentStatus(
                                  (appointement?.attendees?.appointmentStatus).toLowerCase()
                                )}
                                value={
                                  appointement?.attendees?.appointmentStatus
                                }
                                className="py-0.5 w-min px-2 text-[11px] font-medium"
                              />
                            </td>
                          </tr>
                        </>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            ) : (
              <>
                <div className="text-center flex flex-col justify-center items-center mt-3 gap-2">
                  <RiInbox2Line className="text-gray-600 text-xl" />
                  <Typography className="text-gray-600 font-normal">
                    No Data
                  </Typography>
                </div>
              </>
            )}
          </div>
        </CardBody>
        {location.pathname === "/dashboard" && (
          <div className="p-2 border-t">
            <Link className="text-blue-500 text-sm font-medium" to="/seekers">
              <i className="mdi mdi-arrow-right-circle me-1"></i> View All
              Seekers
            </Link>
          </div>
        )}
      </Card>

      <Dialog open={open} handler={toggleModal} size="md">
        <DialogHeader>
          <div className="flex items-center">
            <Typography variant="h6" color="blue-gray">
              Invite Seeker
            </Typography>
          </div>
        </DialogHeader>
        <DialogBody>
          <div className="w-full mb-4">
            <div className="border border-gray-300 rounded-2xl flex p-2">
              <input
                type="text"
                value={seekerMail}
                onChange={(e) => setSeekerMail(e.target.value)}
                className="flex-grow p-2 bg-transparent outline-none"
              />
            </div>
            <div className="flex justify-center mt-4 space-x-2">
              <Button color="gray" onClick={toggleModal} className="text-white">
                Close
              </Button>
              <Button
                color="blue"
                disabled={seekerMail === ""}
                onClick={handleSubmit}
                className="text-white"
              >
                Send Mail
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </React.Fragment>
  );
};

export default SeekerPriority;
